/**
 * StudentModulePodcast — Podcast Mode player.
 *
 * Plays a whole module's questions back-to-back like a podcast. Uses two
 * HTMLAudioElements in ping-pong: while element A plays, element B fetches
 * (with auth Bearer token) the next item's MP3 blob. When A's `ended`
 * fires we swap to B and prefetch item N+1 into A. This yields near-gapless
 * playback without a Web Audio pipeline — ~30-50 ms swap gap in practice.
 *
 * Content per item: `_build_podcast_script(q)` on the backend — question
 * stem + choices + "The answer is X. <choice text>". No AI explanation
 * (P0 scope 2026-02).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ChevronLeft, Headphones, Play, Pause, SkipBack, SkipForward, Settings2,
  Loader2, Volume2, Rewind, FastForward,
} from "lucide-react";
import {
  isMediaSessionSupported, setMediaMetadata, setPlaybackState,
  setMediaHandlers, clearMediaSession,
} from "@/lib/mediaSession";
import { useWakeLock } from "@/lib/wakeLock";

/**
 * Podcast-specific settings. Persisted to localStorage, INDEPENDENT of the
 * Review Mode audio settings the student saves via /student/audio-settings.
 * Users listen to podcasts differently (usually faster, with a beat between
 * items to breathe) so overloading the review-mode settings didn't work.
 */
const PODCAST_SETTINGS_KEY = "pm_podcast_settings";
const PODCAST_VOICES = [
  { v: "nova", label: "Nova (female, warm)" },
  { v: "alloy", label: "Alloy (neutral)" },
  { v: "echo", label: "Echo (male, calm)" },
  { v: "onyx", label: "Onyx (male, deep)" },
  { v: "fable", label: "Fable (male, British)" },
  { v: "shimmer", label: "Shimmer (female, bright)" },
];
const PODCAST_SPEEDS = [
  { v: 0.75, label: "0.75× — very slow" },
  { v: 0.9,  label: "0.9× — slow (matches Commute)" },
  { v: 1.0,  label: "1.0× — normal" },
  { v: 1.15, label: "1.15× — fast" },
  { v: 1.25, label: "1.25× — very fast" },
  { v: 1.5,  label: "1.5× — sprint" },
];
const PODCAST_GAPS = [
  { v: 0,    label: "No gap (back-to-back)" },
  { v: 500,  label: "0.5 s — quick breath" },
  { v: 1000, label: "1 s — comfortable" },
  { v: 2000, label: "2 s — think it over" },
  { v: 3000, label: "3 s — deep pause" },
  { v: 5000, label: "5 s — long pause" },
  { v: 8000, label: "8 s — study break" },
  { v: 10000, label: "10 s — quiz yourself" },
];
const DEFAULT_PODCAST_SETTINGS = {
  voice: "nova",
  speed: 0.9,
  gap_ms: 1000,                    // gap between questions (item→item)
  gap_after_question_ms: 2000,     // pause after the stem before choices
  gap_after_choices_ms: 3000,      // pause after choices before answer (think time)
  gap_after_answer_ms: 1000,       // pause after answer before next question
};

function loadPodcastSettings() {
  try {
    const raw = localStorage.getItem(PODCAST_SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_PODCAST_SETTINGS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PODCAST_SETTINGS, ...parsed };
  } catch { return { ...DEFAULT_PODCAST_SETTINGS }; }
}

// Ordered segment kinds for a podcast item — the three optional audio
// URLs (question stem, choices, correct-answer) that the backend returns.
const SEG_KINDS = ["question", "choices", "answer"];

const DEFAULT_AUDIO = {
  read_aloud_enabled: false,
  reading_flow: "manual_next",
  delay_before_answer_seconds: 5,
  delay_before_next_question_seconds: 5,
  reading_speed: "normal",
  selected_voice: "default",
};

function fmt(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function StudentModulePodcast() {
  const { moduleId } = useParams();
  const navigate = useNavigate();

  const [settings, setSettings] = useState(DEFAULT_AUDIO);
  // Podcast-specific settings — SEPARATE from review-mode audio settings.
  // Persisted locally so power users can dial in their commute pace without
  // affecting how Review Mode reads answers back to them.
  const [podcastSettings, setPodcastSettings] = useState(loadPodcastSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [playlist, setPlaylist] = useState(null); // { module_title, items: [...] }
  const [idx, setIdx] = useState(0);
  // Which section of the current item is playing:
  //   0 = question stem, 1 = choices, 2 = answer.
  // Kept in a ref (not state) inside advance() so we can atomically decide
  // the next segment without racing React re-renders.
  const [segIdx, setSegIdx] = useState(0);
  const segIdxRef = useRef(0);
  useEffect(() => { segIdxRef.current = segIdx; }, [segIdx]);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);

  // Two <audio> elements to ping-pong for near-gapless playback.
  const audioA = useRef(null);
  const audioB = useRef(null);
  // Which ref is currently "active" (playing) — we swap this on advance.
  const activeRef = useRef("A");
  // Object-URL cache keyed by playlist index so we don't re-download.
  const blobsRef = useRef(new Map());
  // Track in-flight fetches so cancelSpeech-style teardown is deterministic.
  const abortersRef = useRef(new Map());
  const wakeLock = useWakeLock(playing);
  const wakeActive = wakeLock?.locked;

  // Load student audio settings once — voice + speed feed the playlist URL.
  useEffect(() => {
    api.get("/student/audio-settings")
      .then((r) => setSettings({ ...DEFAULT_AUDIO, ...(r.data || {}) }))
      .catch(() => setSettings(DEFAULT_AUDIO));
  }, []);

  // Load playlist whenever podcast settings change (voice / speed).
  // Uses the podcast-specific settings NOT the review-mode ones — the two
  // are decoupled so podcast pace can differ from Commute Mode's pace.
  useEffect(() => {
    let cancelled = false;
    api.get(`/modules/${moduleId}/podcast`, { params: {
      voice: podcastSettings.voice,
      speed: podcastSettings.speed,
    } })
      .then((r) => {
        if (cancelled) return;
        if (!r.data?.items?.length) {
          setError("No questions available for podcast in this module yet.");
          return;
        }
        setPlaylist(r.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Failed to load podcast playlist.");
      });
    return () => { cancelled = true; };
  }, [moduleId, podcastSettings.voice, podcastSettings.speed]);

  // Persist podcast settings on every change so they survive a reload.
  useEffect(() => {
    try { localStorage.setItem(PODCAST_SETTINGS_KEY, JSON.stringify(podcastSettings)); }
    catch { /* localStorage full / private mode — ignore */ }
    // Invalidate the blob cache when voice/speed change since URLs are keyed
    // on those params server-side. Otherwise old (wrong-voice) blobs would keep
    // playing until page reload.
    blobsRef.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* noop */ } });
    blobsRef.current.clear();
  }, [podcastSettings.voice, podcastSettings.speed]);

  // ---------------------------------------------------------------------
  // Segment model — each playlist item has up to 3 audio segments (stem,
  // choices, answer). We flatten the playlist into a linear list of
  // { itemIdx, segIdx, segKind, url } tracks so the ping-pong player can
  // walk them one by one while a configurable pause is inserted between
  // segments based on `podcastSettings.gap_after_*_ms`.
  // ---------------------------------------------------------------------

  const tracks = useMemo(() => {
    if (!playlist?.items) return [];
    const out = [];
    playlist.items.forEach((it, itemIdx) => {
      const segs = [
        it.question_audio_url,
        it.choices_audio_url,
        it.answer_audio_url,
      ];
      // Backwards compat: if the backend didn't return per-section URLs,
      // fall back to the single combined audio_url as one big segment.
      const anySeg = segs.some((u) => !!u);
      if (!anySeg) {
        out.push({ itemIdx, segIdx: 0, segKind: "full", url: it.audio_url });
        return;
      }
      segs.forEach((url, segIdx) => {
        if (url) out.push({ itemIdx, segIdx, segKind: SEG_KINDS[segIdx], url });
      });
    });
    return out;
  }, [playlist]);

  // Compute the flat track index for a given (itemIdx, segIdx). Falls back
  // to the first track of the item if that segment isn't available (e.g.
  // question with no answer, or legacy fallback item).
  const findTrackIdx = useCallback((itemIdx, segIdx) => {
    if (!tracks.length) return -1;
    const exact = tracks.findIndex((t) => t.itemIdx === itemIdx && t.segIdx === segIdx);
    if (exact !== -1) return exact;
    return tracks.findIndex((t) => t.itemIdx === itemIdx);
  }, [tracks]);

  // Fetch a track's blob URL (auth-token headers included), memoised.
  // Blob cache keyed by flat track index — segments coexist per item.
  const fetchTrackBlob = useCallback(async (i) => {
    if (i < 0 || i >= tracks.length) return null;
    const cached = blobsRef.current.get(i);
    if (cached) return cached;
    const track = tracks[i];
    if (!track?.url) return null;
    const controller = new AbortController();
    abortersRef.current.set(i, controller);
    const token = localStorage.getItem("pm_token");
    // audio_url already looks like `/api/tts/audio?text=...&voice=...&speed=...`
    const url = `${API}${track.url.replace(/^\/api/, "")}`;
    const res = await fetch(url, {
      signal: controller.signal,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => "");
      throw new Error(`Audio ${res.status}: ${msg.slice(0, 100) || res.statusText}`);
    }
    const blob = await res.blob();
    const objURL = URL.createObjectURL(blob);
    blobsRef.current.set(i, objURL);
    return objURL;
  }, [tracks]);

  const getActiveAudio = () => (activeRef.current === "A" ? audioA.current : audioB.current);
  const getIdleAudio = () => (activeRef.current === "A" ? audioB.current : audioA.current);

  // Prefetch flat track `nextTrackIdx` into the idle audio element.
  const preloadNext = useCallback(async (nextTrackIdx) => {
    if (nextTrackIdx < 0 || nextTrackIdx >= tracks.length) return;
    try {
      const url = await fetchTrackBlob(nextTrackIdx);
      const idle = getIdleAudio();
      if (idle && url && idle.src !== url) {
        idle.src = url;
        idle.load();
      }
    } catch (e) {
      // Non-fatal — playback of the CURRENT track continues; we'll retry on
      // swap.
      console.warn("[Podcast] preload failed for track", nextTrackIdx, e?.message || e);
    }
  }, [tracks, fetchTrackBlob]);

  // Play the given (itemIdx, segIdx) on the active audio element.
  const playAt = useCallback(async (itemIdx, segIdx = 0) => {
    const trackIdx = findTrackIdx(itemIdx, segIdx);
    if (trackIdx === -1) return;
    setBuffering(true);
    setError(null);
    try {
      const url = await fetchTrackBlob(trackIdx);
      const audio = getActiveAudio();
      if (!audio || !url) return;
      if (audio.src !== url) {
        audio.src = url;
        audio.load();
      }
      audio.playbackRate = 1.0;
      await audio.play();
      setPlaying(true);
      setPlaybackState("playing");
      preloadNext(trackIdx + 1);
    } catch (e) {
      setError(e?.message || "Playback failed");
      setPlaying(false);
      setPlaybackState("paused");
    } finally {
      setBuffering(false);
    }
  }, [findTrackIdx, fetchTrackBlob, preloadNext]);

  // Return the pause (ms) that should follow the segment that just ended.
  const gapAfterCurrentSegment = useCallback((currentItemIdx, currentSegIdx, nextItemIdx) => {
    // Crossing item boundary → use inter-item gap.
    if (nextItemIdx !== currentItemIdx) return Math.max(0, Number(podcastSettings.gap_ms) || 0);
    // Within the same item, use the per-section gap based on which
    // segment we just finished.
    if (currentSegIdx === 0) return Math.max(0, Number(podcastSettings.gap_after_question_ms) || 0);
    if (currentSegIdx === 1) return Math.max(0, Number(podcastSettings.gap_after_choices_ms) || 0);
    if (currentSegIdx === 2) return Math.max(0, Number(podcastSettings.gap_after_answer_ms) || 0);
    return 0;
  }, [podcastSettings]);

  // Advance to the next flat track (segment). Called from the audio
  // element's `ended` event.
  const advance = useCallback(async () => {
    const curTrackIdx = findTrackIdx(idx, segIdxRef.current);
    if (curTrackIdx === -1) return;
    const nextTrackIdx = curTrackIdx + 1;
    if (nextTrackIdx >= tracks.length) {
      setPlaying(false);
      setPlaybackState("none");
      toast.success("Podcast complete. Great commute session!");
      return;
    }
    const cur = tracks[curTrackIdx];
    const nxt = tracks[nextTrackIdx];
    const gap = gapAfterCurrentSegment(cur.itemIdx, cur.segIdx, nxt.itemIdx);
    if (gap > 0) {
      setPlaybackState("paused");
      await new Promise((r) => setTimeout(r, gap));
    }
    // Swap active/idle. Idle likely has the next blob pre-loaded already.
    activeRef.current = activeRef.current === "A" ? "B" : "A";
    setIdx(nxt.itemIdx);
    setSegIdx(nxt.segIdx);
    const audio = getActiveAudio();
    try {
      if (!audio.src) {
        await playAt(nxt.itemIdx, nxt.segIdx);
      } else {
        audio.playbackRate = 1.0;
        await audio.play();
        setPlaying(true);
        setPlaybackState("playing");
      }
    } catch (e) {
      await playAt(nxt.itemIdx, nxt.segIdx);
    }
    preloadNext(nextTrackIdx + 1);
  }, [tracks, idx, findTrackIdx, gapAfterCurrentSegment, playAt, preloadNext]);

  const goto = useCallback(async (i) => {
    if (!playlist) return;
    const clamped = Math.max(0, Math.min(playlist.items.length - 1, i));
    if (clamped === idx && segIdxRef.current === 0) return;
    // Stop currently playing element (both, to be safe) then play new item
    // from segment 0 on the current active element.
    try { audioA.current?.pause(); } catch { /* noop */ }
    try { audioB.current?.pause(); } catch { /* noop */ }
    setIdx(clamped);
    setSegIdx(0);
    await playAt(clamped, 0);
  }, [playlist, idx, playAt]);

  const togglePlay = useCallback(async () => {
    const audio = getActiveAudio();
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      setPlaybackState("paused");
    } else {
      // If we've never started, kick off item 0 segment 0 fresh.
      if (!audio.src) {
        await playAt(idx, segIdxRef.current || 0);
      } else {
        try {
          await audio.play();
          setPlaying(true);
          setPlaybackState("playing");
        } catch (e) {
          setError(e?.message || "Playback blocked. Tap Play again.");
        }
      }
    }
  }, [playing, idx, playAt]);

  const seek = useCallback((delta) => {
    const audio = getActiveAudio();
    if (!audio || !Number.isFinite(audio.duration)) return;
    audio.currentTime = Math.max(0, Math.min(audio.duration, audio.currentTime + delta));
  }, []);

  // Bind event listeners to BOTH audio elements — timeupdate/ended fire on
  // whichever one is currently active; the other stays silent-preloaded.
  useEffect(() => {
    const bind = (el) => {
      if (!el) return () => {};
      const onEnded = () => {
        if (getActiveAudio() === el) advance();
      };
      const onTime = () => {
        if (getActiveAudio() === el) {
          setCurrentTime(el.currentTime || 0);
          setDuration(el.duration || 0);
        }
      };
      const onPlay = () => { if (getActiveAudio() === el) setPlaying(true); };
      const onPause = () => { if (getActiveAudio() === el) setPlaying(false); };
      const onWaiting = () => { if (getActiveAudio() === el) setBuffering(true); };
      const onPlaying = () => { if (getActiveAudio() === el) setBuffering(false); };
      const onError = () => { if (getActiveAudio() === el) setError("Audio playback failed"); };
      el.addEventListener("ended", onEnded);
      el.addEventListener("timeupdate", onTime);
      el.addEventListener("loadedmetadata", onTime);
      el.addEventListener("play", onPlay);
      el.addEventListener("pause", onPause);
      el.addEventListener("waiting", onWaiting);
      el.addEventListener("playing", onPlaying);
      el.addEventListener("error", onError);
      return () => {
        el.removeEventListener("ended", onEnded);
        el.removeEventListener("timeupdate", onTime);
        el.removeEventListener("loadedmetadata", onTime);
        el.removeEventListener("play", onPlay);
        el.removeEventListener("pause", onPause);
        el.removeEventListener("waiting", onWaiting);
        el.removeEventListener("playing", onPlaying);
        el.removeEventListener("error", onError);
      };
    };
    const offA = bind(audioA.current);
    const offB = bind(audioB.current);
    return () => { offA(); offB(); };
  }, [advance]);

  // Wire up OS lock-screen media controls to our play/pause/skip.
  useEffect(() => {
    if (!isMediaSessionSupported() || !playlist) return undefined;
    setMediaMetadata({
      title: playlist.module_title || "PrepMasters Podcast",
      artist: `Question ${idx + 1} of ${playlist.items.length}`,
      album: "PrepMasters · Podcast Mode",
    });
    setMediaHandlers({
      play: () => togglePlay(),
      pause: () => togglePlay(),
      previoustrack: () => goto(idx - 1),
      nexttrack: () => goto(idx + 1),
      seekforward: () => seek(15),
      seekbackward: () => seek(-15),
    });
    return () => setMediaHandlers({});
  }, [playlist, idx, togglePlay, goto, seek]);

  // Full teardown on unmount.
  useEffect(() => {
    return () => {
      try { audioA.current?.pause(); } catch { /* noop */ }
      try { audioB.current?.pause(); } catch { /* noop */ }
      abortersRef.current.forEach((c) => { try { c.abort(); } catch { /* noop */ } });
      blobsRef.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* noop */ } });
      blobsRef.current.clear();
      clearMediaSession();
    };
  }, []);

  const current = useMemo(() => playlist?.items?.[idx] || null, [playlist, idx]);
  const total = playlist?.items?.length || 0;

  return (
    <div className="space-y-6" data-testid="student-module-podcast">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
        data-testid="podcast-back-btn"
      >
        <ChevronLeft className="w-4 h-4" />Back
      </button>

      {/* Hero */}
      <div className="rounded-2xl p-6 bg-gradient-to-br from-amber-50 via-orange-50 to-white border border-amber-100">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center shadow-sm">
            <Headphones className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">Podcast Mode</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight truncate">
              {playlist?.module_title || "Loading…"}
            </h1>
            {playlist && (
              <div className="text-sm text-slate-600 mt-1">
                {total} question{total === 1 ? "" : "s"} · voice: <b>{podcastSettings.voice}</b> · speed: <b>{podcastSettings.speed}×</b>
              </div>
            )}
          </div>
          {/* Podcast-specific settings popover — INDEPENDENT of Review Mode
              audio settings so students can dial in the podcast pace
              (voice, speed, inter-item gap) without touching how
              Commute Mode reads their answers. Persisted to localStorage. */}
          <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="shrink-0 h-10 gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="podcast-settings-btn">
                <Settings2 className="w-4 h-4" />
                <span className="hidden sm:inline">Podcast Settings</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-4 space-y-3" data-testid="podcast-settings-popover">
              <div className="font-display font-bold text-sm flex items-center gap-1.5">
                <Settings2 className="w-4 h-4 text-amber-600" /> Podcast Settings
              </div>
              <p className="text-[11px] text-slate-500 leading-snug">
                These settings are for Podcast Mode only — they don't affect Review Mode or Commute Mode.
              </p>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Voice</label>
                <Select
                  value={podcastSettings.voice}
                  onValueChange={(v) => setPodcastSettings((s) => ({ ...s, voice: v }))}
                >
                  <SelectTrigger className="h-9" data-testid="podcast-voice-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PODCAST_VOICES.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Speed</label>
                <Select
                  value={String(podcastSettings.speed)}
                  onValueChange={(v) => setPodcastSettings((s) => ({ ...s, speed: Number(v) }))}
                >
                  <SelectTrigger className="h-9" data-testid="podcast-speed-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PODCAST_SPEEDS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Gap after question stem</label>
                <Select
                  value={String(podcastSettings.gap_after_question_ms)}
                  onValueChange={(v) => setPodcastSettings((s) => ({ ...s, gap_after_question_ms: Number(v) }))}
                >
                  <SelectTrigger className="h-9" data-testid="podcast-gap-after-question-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PODCAST_GAPS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500">Time to think before hearing the choices.</p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Gap after choices</label>
                <Select
                  value={String(podcastSettings.gap_after_choices_ms)}
                  onValueChange={(v) => setPodcastSettings((s) => ({ ...s, gap_after_choices_ms: Number(v) }))}
                >
                  <SelectTrigger className="h-9" data-testid="podcast-gap-after-choices-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PODCAST_GAPS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500">Time to pick your answer before it's revealed.</p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Gap after answer</label>
                <Select
                  value={String(podcastSettings.gap_after_answer_ms)}
                  onValueChange={(v) => setPodcastSettings((s) => ({ ...s, gap_after_answer_ms: Number(v) }))}
                >
                  <SelectTrigger className="h-9" data-testid="podcast-gap-after-answer-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PODCAST_GAPS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500">Rest before the next question starts.</p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Gap between questions</label>
                <Select
                  value={String(podcastSettings.gap_ms)}
                  onValueChange={(v) => setPodcastSettings((s) => ({ ...s, gap_ms: Number(v) }))}
                >
                  <SelectTrigger className="h-9" data-testid="podcast-gap-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PODCAST_GAPS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500">Overall breather between one question and the next.</p>
              </div>

              <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
                Changing voice or speed re-fetches the audio (~1-3 s per item). Gap is instant.
              </div>

              <Button variant="outline" size="sm" className="w-full h-9" onClick={() => setPodcastSettings({ ...DEFAULT_PODCAST_SETTINGS })} data-testid="podcast-reset-btn">
                Reset to defaults
              </Button>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {error && (
        <Card className="p-4 border-red-200 bg-red-50 text-red-800 text-sm" data-testid="podcast-error">
          {error}
        </Card>
      )}

      {/* Current item */}
      <Card className="p-6 border-slate-200 min-h-[140px]" data-testid="podcast-current-card">
        {!playlist ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading playlist…
          </div>
        ) : current ? (
          <>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Now playing · Question {idx + 1} of {total}
            </div>
            <div className="mt-2 text-base sm:text-lg text-slate-900 leading-snug line-clamp-4" data-testid="podcast-current-stem">
              {current.stem}
            </div>
          </>
        ) : null}
      </Card>

      {/* Progress bar */}
      <div className="space-y-2" data-testid="podcast-progress">
        <Slider
          value={[duration ? (currentTime / duration) * 100 : 0]}
          onValueChange={(v) => {
            const audio = getActiveAudio();
            if (audio && Number.isFinite(audio.duration)) {
              audio.currentTime = (v[0] / 100) * audio.duration;
            }
          }}
          className="w-full"
          data-testid="podcast-seek"
        />
        <div className="flex items-center justify-between text-xs text-slate-500 font-mono">
          <span>{fmt(currentTime)}</span>
          <span>{fmt(duration)}</span>
        </div>
      </div>

      {/* Transport controls */}
      <div className="flex items-center justify-center gap-3">
        <Button
          variant="outline" className="h-11 w-11 p-0 rounded-full"
          onClick={() => goto(idx - 1)}
          disabled={!playlist || idx === 0}
          data-testid="podcast-prev-btn"
          aria-label="Previous question"
        >
          <SkipBack className="w-5 h-5" />
        </Button>
        <Button
          variant="outline" className="h-11 w-11 p-0 rounded-full"
          onClick={() => seek(-15)}
          disabled={!playlist}
          data-testid="podcast-rewind-btn"
          aria-label="Rewind 15 seconds"
        >
          <Rewind className="w-5 h-5" />
        </Button>
        <Button
          className="h-14 w-14 p-0 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-300/50"
          onClick={togglePlay}
          disabled={!playlist}
          data-testid="podcast-play-toggle"
          aria-label={playing ? "Pause" : "Play"}
        >
          {buffering ? <Loader2 className="w-6 h-6 animate-spin" /> :
            playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
        </Button>
        <Button
          variant="outline" className="h-11 w-11 p-0 rounded-full"
          onClick={() => seek(15)}
          disabled={!playlist}
          data-testid="podcast-forward-btn"
          aria-label="Forward 15 seconds"
        >
          <FastForward className="w-5 h-5" />
        </Button>
        <Button
          variant="outline" className="h-11 w-11 p-0 rounded-full"
          onClick={() => goto(idx + 1)}
          disabled={!playlist || idx >= total - 1}
          data-testid="podcast-next-btn"
          aria-label="Next question"
        >
          <SkipForward className="w-5 h-5" />
        </Button>
      </div>

      {/* Playlist / jump-to */}
      {playlist && (
        <Card className="p-2 border-slate-200 max-h-72 overflow-y-auto" data-testid="podcast-playlist">
          {playlist.items.map((it, i) => (
            <button
              key={it.question_id}
              onClick={() => goto(i)}
              className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-3 transition-colors ${
                i === idx
                  ? "bg-amber-50 text-amber-900"
                  : "hover:bg-slate-50 text-slate-700"
              }`}
              data-testid={`podcast-item-${i}`}
            >
              <span className={`w-6 shrink-0 text-xs font-mono ${i === idx ? "text-amber-600 font-bold" : "text-slate-400"}`}>
                {String(i + 1).padStart(2, "0")}
              </span>
              {i === idx && playing ? (
                <Volume2 className="w-4 h-4 text-amber-600 shrink-0 animate-pulse" />
              ) : (
                <span className="w-4 shrink-0" />
              )}
              <span className="text-sm truncate flex-1">{it.stem}</span>
            </button>
          ))}
        </Card>
      )}

      {/* Hidden audio elements — two of them for gapless swap. */}
      <audio ref={audioA} preload="auto" data-testid="podcast-audio-a" />
      <audio ref={audioB} preload="auto" data-testid="podcast-audio-b" />

      {/* WakeLock is applied via the hook; nothing to render here. */}
      <div className="text-center text-xs text-slate-400 pt-2">
        <Link to="/student/courses" className="hover:text-slate-600">← Back to courses</Link>
        {wakeActive ? <span className="ml-3">· screen kept on while playing</span> : null}
      </div>
    </div>
  );
}
